"use client";

import clsx from "clsx";
import React, {useMemo} from "react";
import {useTranslation} from "react-i18next";
import MarkdownRenderer from "@/components/common/MarkdownRenderer";
import {usePreviewFetch} from "@/hooks/useFilePreview";
import {parseTextFromResponse} from "@/utils/filePreview";

const MAX_TEXT_BYTES = 2 * 1024 * 1024;
// Layout a huge file as a sequence of smaller <pre> blocks; browsers lay them
// out incrementally, avoiding the main-thread freeze of one multi-MB node.
const CHUNK_CHARS = 64 * 1024;

const isMarkdown = (fileName: string, mimeType: string): boolean => {
   const base = mimeType.split(";")[0].trim().toLowerCase();
   if (base === "text/markdown" || base === "text/x-markdown") return true;
   const ext = fileName.split(".").pop()?.toLowerCase();
   return ext === "md" || ext === "markdown";
};

const chunkText = (content: string): string[] => {
   if (content.length <= CHUNK_CHARS) return [content];
   const chunks: string[] = [];
   for (let i = 0; i < content.length; i += CHUNK_CHARS) {
      chunks.push(content.slice(i, i + CHUNK_CHARS));
   }
   return chunks;
};

const InlineTextPreview: React.FC<{
   url: string;
   fileName: string;
   mimeType: string;
}> = ({url, fileName, mimeType}) => {
   const {t} = useTranslation("ask");
   const {data, error, loading} = usePreviewFetch(url, (res) =>
      parseTextFromResponse(res, MAX_TEXT_BYTES)
   );

   const chunks = useMemo(
      () => (data?.content ? chunkText(data.content) : []),
      [data?.content]
   );

   if (error) {
      return (
         <div className="flex items-center justify-center h-full text-500 text-sm">
            {t("preview.failedToLoad")}
         </div>
      );
   }

   if (loading || !data) {
      return (
         <div className="relative w-full h-full">
            <div className="absolute inset-0 z-20 flex items-center justify-center bg-100">
               <div className="w-8 h-8 border-2 border-300 border-t-primary rounded-full animate-spin" />
            </div>
         </div>
      );
   }

   if (data.content.length === 0) {
      return (
         <div className="flex items-center justify-center h-full text-500 text-sm">
            {t("preview.emptyFile")}
         </div>
      );
   }

   if (isMarkdown(fileName, mimeType)) {
      return (
         <div className="w-full h-full overflow-auto bg-panel p-6">
            <div className="max-w-[720px] mx-auto">
               <MarkdownRenderer content={data.content} />
               {data.truncated && (
                  <p className="mt-4 text-xs text-500 italic">{t("preview.truncated")}</p>
               )}
            </div>
         </div>
      );
   }

   return (
      <div className="w-full h-full overflow-auto bg-panel">
         {chunks.map((chunk, i) => (
            <pre
               key={i}
               className={clsx(
                  "text-xs font-mono leading-5 text-800 whitespace-pre-wrap break-words",
                  "px-4 m-0",
                  i === 0 && "pt-4",
                  i === chunks.length - 1 && "pb-4"
               )}
            >
               {chunk}
            </pre>
         ))}
         {data.truncated && (
            <p className="px-4 pb-4 text-xs text-500 italic">{t("preview.truncated")}</p>
         )}
      </div>
   );
};

export default InlineTextPreview;
